import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  TrendingUp, Users, Building, DollarSign, Target, Phone, Mail, MapPin,
  LogOut, Loader2, RefreshCw, Calendar, Clock, Eye, UserPlus, Filter,
  LayoutDashboard, FileText, BarChart3, Menu, ChevronRight, X, History,
  Bell, AlertTriangle, Send
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEAD_STATUSES = [
  'New Lead', 'Contacted', 'Meeting Scheduled', 'Proposal Sent', 'Negotiation', 'Converted', 'Lost'
];

const STATUS_COLORS = {
  'New Lead': 'bg-blue-100 text-blue-700',
  'Contacted': 'bg-purple-100 text-purple-700',
  'Meeting Scheduled': 'bg-yellow-100 text-yellow-700',
  'Proposal Sent': 'bg-cyan-100 text-cyan-700',
  'Negotiation': 'bg-orange-100 text-orange-700',
  'Converted': 'bg-green-100 text-green-700',
  'Lost': 'bg-red-100 text-red-700'
};

const SalesAdminPortal = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [leads, setLeads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [dailyReport, setDailyReport] = useState(null);
  const [followups, setFollowups] = useState({ overdue: [], upcoming: [] });
  const [activeTab, setActiveTab] = useState('dashboard');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadHistory, setLeadHistory] = useState([]);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignEmployeeId, setAssignEmployeeId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  
  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);

  const userName = localStorage.getItem('salesUserName') || 'Sales Admin';

  useEffect(() => {
    const role = localStorage.getItem('userRole');
    if (role !== 'sales_admin') {
      navigate('/admin');
      return;
    }
    fetchAllData();
  }, [navigate]);

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      const [statsRes, leadsRes, employeesRes, performanceRes, followupsRes] = await Promise.all([
        fetch(`${API_URL}/api/sales/dashboard-stats`),
        fetch(`${API_URL}/api/sales/leads`),
        fetch(`${API_URL}/api/sales/employees`),
        fetch(`${API_URL}/api/sales/employee-performance`),
        fetch(`${API_URL}/api/sales/followups?days=7`)
      ]);

      const [statsData, leadsData, employeesData, performanceData, followupsData] = await Promise.all([
        statsRes.json(), leadsRes.json(), employeesRes.json(), performanceRes.json(), followupsRes.json()
      ]);

      if (statsData.success) setStats(statsData.stats);
      if (leadsData.success) setLeads(leadsData.leads);
      if (employeesData.success) setEmployees(employeesData.employees);
      if (performanceData.success) setPerformance(performanceData.performance);
      if (followupsData.success) setFollowups({ overdue: followupsData.overdue, upcoming: followupsData.upcoming });
      
      // Fetch daily report
      fetchDailyReport(filterDate);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDailyReport = async (date) => {
    try {
      const response = await fetch(`${API_URL}/api/sales/daily-report?date=${date}`);
      const data = await response.json();
      if (data.success) setDailyReport(data);
    } catch (error) {
      console.error('Error fetching daily report:', error);
    }
  };

  const sendFollowupReminders = async () => {
    setSendingReminders(true);
    try {
      const response = await fetch(`${API_URL}/api/sales/send-followup-reminders?days=1`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        alert(`${data.message}`);
      } else {
        alert('Failed to send reminders');
      }
    } catch (error) {
      console.error('Error sending reminders:', error);
      alert('Error sending reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  const fetchLeadHistory = async (leadId) => {
    try {
      const response = await fetch(`${API_URL}/api/sales/leads/${leadId}`);
      const data = await response.json();
      if (data.success) {
        setSelectedLead(data.lead);
        setLeadHistory(data.activities);
        setShowLeadModal(true);
      }
    } catch (error) {
      console.error('Error fetching lead history:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('salesToken');
    localStorage.removeItem('salesUserId');
    localStorage.removeItem('salesUserName');
    localStorage.removeItem('userRole');
    navigate('/admin');
  };

  const handleAssignLead = async () => {
    if (!assignEmployeeId || !selectedLead) return;
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/sales/leads/${selectedLead.id}/assign`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employee_id: assignEmployeeId })
      });
      const data = await response.json();
      if (data.success) {
        alert('Lead assigned successfully!');
        setShowAssignModal(false);
        setAssignEmployeeId('');
        fetchAllData();
        fetchLeadHistory(selectedLead.id);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const filteredLeads = leads.filter(lead => {
    if (filterStatus && lead.status !== filterStatus) return false;
    if (filterEmployee && lead.assigned_to !== filterEmployee) return false;
    return true;
  });

  const sidebarItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'followups', label: 'Follow-ups', icon: Bell, badge: (followups.overdue?.length || 0) + (followups.upcoming?.length || 0) },
    { id: 'leads', label: 'All Leads', icon: Building },
    { id: 'performance', label: 'Employee Performance', icon: BarChart3 },
    { id: 'reports', label: 'Daily Reports', icon: FileText },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100" data-testid="loading-spinner">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 transform transition-transform duration-300 ease-in-out ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:static lg:inset-auto`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-white font-bold text-lg">Sales CRM</h1>
                <p className="text-slate-400 text-xs">STEAM-IE</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {sidebarItems.map((item) => (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); setMobileSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  activeTab === item.id 
                    ? 'bg-orange-600 text-white' 
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium flex-1 text-left">{item.label}</span>
                {item.badge > 0 && (
                  <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${
                    activeTab === item.id ? 'bg-white text-orange-600' : 'bg-red-500 text-white'
                  }`}>
                    {item.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t border-slate-700">
            <div className="flex items-center gap-3 mb-4 px-2">
              <div className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold">
                {userName.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">{userName}</p>
                <p className="text-slate-400 text-xs">Sales Admin</p>
              </div>
            </div>
            <Button onClick={handleLogout} variant="outline" className="w-full border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        {/* Top Bar */}
        <header className="bg-white shadow-sm sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <button onClick={() => setMobileSidebarOpen(true)} className="lg:hidden p-2 hover:bg-gray-100 rounded-lg">
                <Menu className="w-6 h-6" />
              </button>
              <h2 className="text-xl font-bold text-gray-900 capitalize">{activeTab === 'dashboard' ? 'Dashboard' : activeTab}</h2>
            </div>
            <Button onClick={fetchAllData} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </header>

        {/* Content Area */}
        <div className="p-4 lg:p-8">
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100 text-sm">Total Leads</p>
                        <p className="text-3xl font-bold">{stats.total_leads || 0}</p>
                      </div>
                      <Building className="w-10 h-10 text-blue-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-100 text-sm">Converted</p>
                        <p className="text-3xl font-bold">{stats.converted || 0}</p>
                      </div>
                      <Target className="w-10 h-10 text-green-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-orange-100 text-sm">Conversion Rate</p>
                        <p className="text-3xl font-bold">{stats.conversion_rate || 0}%</p>
                      </div>
                      <TrendingUp className="w-10 h-10 text-orange-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-100 text-sm">Converted Value</p>
                        <p className="text-2xl font-bold">₹{(stats.converted_value || 0).toLocaleString()}</p>
                      </div>
                      <DollarSign className="w-10 h-10 text-purple-200" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Pipeline Stats */}
              <Card>
                <CardHeader>
                  <CardTitle>Sales Pipeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
                    {LEAD_STATUSES.map((status) => {
                      const count = status === 'New Lead' ? stats.new_leads :
                                   status === 'Contacted' ? stats.contacted :
                                   status === 'Meeting Scheduled' ? stats.meetings :
                                   status === 'Proposal Sent' ? stats.proposals :
                                   status === 'Negotiation' ? stats.negotiations :
                                   status === 'Converted' ? stats.converted :
                                   status === 'Lost' ? stats.lost : 0;
                      return (
                        <div key={status} className={`p-3 rounded-lg text-center ${STATUS_COLORS[status]}`}>
                          <p className="text-2xl font-bold">{count || 0}</p>
                          <p className="text-xs font-medium">{status}</p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Leads */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Recent Leads</CardTitle>
                  <Button variant="link" onClick={() => setActiveTab('leads')}>View All</Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {leads.slice(0, 5).map((lead) => (
                      <div key={lead.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer" onClick={() => fetchLeadHistory(lead.id)}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold">
                            {lead.school_name?.charAt(0)}
                          </div>
                          <div>
                            <p className="font-medium">{lead.school_name}</p>
                            <p className="text-sm text-gray-500">{lead.city} • {lead.assigned_to_name}</p>
                          </div>
                        </div>
                        <Badge className={STATUS_COLORS[lead.status]}>{lead.status}</Badge>
                      </div>
                    ))}
                    {leads.length === 0 && (
                      <p className="text-center text-gray-500 py-4">No leads yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Follow-ups Tab */}
          {activeTab === 'followups' && (
            <div className="space-y-6">
              {/* Actions Bar */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold">Follow-up Reminders</h3>
                      <p className="text-sm text-gray-500">Send email reminders to employees for upcoming and overdue follow-ups</p>
                    </div>
                    <Button 
                      onClick={sendFollowupReminders} 
                      disabled={sendingReminders}
                      className="bg-orange-600 hover:bg-orange-700"
                    >
                      {sendingReminders ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Send Reminders (Today/Overdue)
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Overdue Follow-ups */}
              <Card className="border-red-200">
                <CardHeader className="bg-red-50">
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <AlertTriangle className="w-5 h-5" />
                    Overdue Follow-ups ({followups.overdue?.length || 0})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {followups.overdue?.map((lead) => (
                      <div key={lead.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center text-red-600 font-bold">
                            {Math.abs(lead.days_until_followup)}d
                          </div>
                          <div>
                            <p className="font-medium">{lead.school_name}</p>
                            <p className="text-sm text-gray-500">{lead.assigned_to_name} • {lead.city}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-sm text-red-600 font-medium">{lead.follow_up_date}</p>
                            <Badge className={STATUS_COLORS[lead.status]}>{lead.status}</Badge>
                          </div>
                          <Button size="sm" variant="outline" onClick={() => fetchLeadHistory(lead.id)}>
                            <Eye className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {(!followups.overdue || followups.overdue.length === 0) && (
                      <p className="text-center text-gray-500 py-4">No overdue follow-ups</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Upcoming Follow-ups */}
              <Card className="border-orange-200">
                <CardHeader className="bg-orange-50">
                  <CardTitle className="flex items-center gap-2 text-orange-700">
                    <Clock className="w-5 h-5" />
                    Upcoming Follow-ups ({followups.upcoming?.length || 0})
                  </CardTitle>
                  <CardDescription>Follow-ups scheduled in the next 7 days</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {followups.upcoming?.map((lead) => (
                      <div key={lead.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-100">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                            lead.days_until_followup === 0 ? 'bg-red-100 text-red-600' : 'bg-orange-100 text-orange-600'
                          }`}>
                            {lead.days_until_followup === 0 ? 'Today' : `${lead.days_until_followup}d`}
                          </div>
                          <div>
                            <p className="font-medium">{lead.school_name}</p>
                            <p className="text-sm text-gray-500">{lead.assigned_to_name} • {lead.city}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-sm text-orange-600 font-medium">{lead.follow_up_date}</p>
                            <Badge className={STATUS_COLORS[lead.status]}>{lead.status}</Badge>
                          </div>
                          <Button size="sm" variant="outline" onClick={() => fetchLeadHistory(lead.id)}>
                            <Eye className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {(!followups.upcoming || followups.upcoming.length === 0) && (
                      <p className="text-center text-gray-500 py-4">No upcoming follow-ups in the next 7 days</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Leads Tab */}
          {activeTab === 'leads' && (
            <div className="space-y-6">
              {/* Filters */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-wrap gap-4">
                    <div className="flex-1 min-w-[200px]">
                      <Label>Filter by Status</Label>
                      <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                        <SelectTrigger><SelectValue placeholder="All Statuses" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Statuses</SelectItem>
                          {LEAD_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex-1 min-w-[200px]">
                      <Label>Filter by Employee</Label>
                      <Select value={filterEmployee || "all"} onValueChange={(v) => setFilterEmployee(v === "all" ? "" : v)}>
                        <SelectTrigger><SelectValue placeholder="All Employees" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Employees</SelectItem>
                          {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Leads Table */}
              <Card>
                <CardHeader>
                  <CardTitle>All Leads ({filteredLeads.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 border-b">
                          <th className="py-3 px-2">School</th>
                          <th className="py-3 px-2">City</th>
                          <th className="py-3 px-2">POC</th>
                          <th className="py-3 px-2">Type</th>
                          <th className="py-3 px-2">Value</th>
                          <th className="py-3 px-2">Assigned To</th>
                          <th className="py-3 px-2">Status</th>
                          <th className="py-3 px-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLeads.map((lead) => (
                          <tr key={lead.id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-2 font-medium">{lead.school_name}</td>
                            <td className="py-3 px-2">{lead.city}</td>
                            <td className="py-3 px-2">
                              <div>
                                <p>{lead.poc_name}</p>
                                <p className="text-xs text-gray-500">{lead.poc_designation}</p>
                              </div>
                            </td>
                            <td className="py-3 px-2">{lead.school_type}</td>
                            <td className="py-3 px-2">₹{(lead.expected_deal_value || 0).toLocaleString()}</td>
                            <td className="py-3 px-2">{lead.assigned_to_name || 'Unassigned'}</td>
                            <td className="py-3 px-2">
                              <Badge className={STATUS_COLORS[lead.status]}>{lead.status}</Badge>
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => fetchLeadHistory(lead.id)}>
                                  <Eye className="w-3 h-3" />
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => { setSelectedLead(lead); setShowAssignModal(true); }}>
                                  <UserPlus className="w-3 h-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {filteredLeads.length === 0 && (
                      <p className="text-center py-8 text-gray-500">No leads found</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Performance Tab */}
          {activeTab === 'performance' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Employee Performance</CardTitle>
                  <CardDescription>Conversion rates and lead statistics by employee</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 border-b">
                          <th className="py-3 px-2">Employee</th>
                          <th className="py-3 px-2">Total Leads</th>
                          <th className="py-3 px-2">Active</th>
                          <th className="py-3 px-2">Converted</th>
                          <th className="py-3 px-2">Lost</th>
                          <th className="py-3 px-2">Conversion Rate</th>
                          <th className="py-3 px-2">Total Value</th>
                          <th className="py-3 px-2">Converted Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {performance.map((emp) => (
                          <tr key={emp._id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold text-sm">
                                  {emp.employee_name?.charAt(0) || '?'}
                                </div>
                                <div>
                                  <p className="font-medium">{emp.employee_name || emp.assigned_to_name}</p>
                                  <p className="text-xs text-gray-500">{emp.employee_email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-2 font-medium">{emp.total_leads}</td>
                            <td className="py-3 px-2">{emp.active}</td>
                            <td className="py-3 px-2 text-green-600 font-medium">{emp.converted}</td>
                            <td className="py-3 px-2 text-red-600">{emp.lost}</td>
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-2">
                                <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-green-500 rounded-full"
                                    style={{ width: `${Math.min(emp.conversion_rate, 100)}%` }}
                                  />
                                </div>
                                <span className="font-medium">{emp.conversion_rate?.toFixed(1)}%</span>
                              </div>
                            </td>
                            <td className="py-3 px-2">₹{(emp.total_value || 0).toLocaleString()}</td>
                            <td className="py-3 px-2 text-green-600 font-medium">₹{(emp.converted_value || 0).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {performance.length === 0 && (
                      <p className="text-center py-8 text-gray-500">No performance data yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Reports Tab */}
          {activeTab === 'reports' && (
            <div className="space-y-6">
              {/* Date Selector */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-1 max-w-xs">
                      <Label>Select Date</Label>
                      <Input 
                        type="date" 
                        value={filterDate} 
                        onChange={(e) => { setFilterDate(e.target.value); fetchDailyReport(e.target.value); }}
                      />
                    </div>
                    <Button onClick={() => fetchDailyReport(filterDate)} className="mt-6">
                      <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Daily Summary */}
              {dailyReport && (
                <>
                  <Card>
                    <CardHeader>
                      <CardTitle>Daily Summary - {dailyReport.date}</CardTitle>
                      <CardDescription>Total Activities: {dailyReport.total_activities}</CardDescription>
                    </CardHeader>
                  </Card>

                  {/* Per Employee Report */}
                  {dailyReport.employee_reports?.map((empReport) => (
                    <Card key={empReport.employee_id}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold">
                              {empReport.employee_name?.charAt(0)}
                            </div>
                            <div>
                              <CardTitle className="text-lg">{empReport.employee_name}</CardTitle>
                              <CardDescription>
                                {empReport.new_leads} new leads • {empReport.status_updates} updates • {empReport.calls} calls • {empReport.meetings} meetings
                              </CardDescription>
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {empReport.activities.map((activity, idx) => (
                            <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                              <div className="w-2 h-2 mt-2 rounded-full bg-orange-500"></div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <p className="font-medium">{activity.school_name}</p>
                                  <span className="text-xs text-gray-500">
                                    {new Date(activity.timestamp).toLocaleTimeString()}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600">{activity.description}</p>
                                {activity.new_status && (
                                  <Badge className={`${STATUS_COLORS[activity.new_status]} mt-1`}>
                                    Status: {activity.new_status}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  {dailyReport.employee_reports?.length === 0 && (
                    <Card>
                      <CardContent className="py-8 text-center text-gray-500">
                        No activities recorded for this date
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Lead Detail Modal */}
      <Dialog open={showLeadModal} onOpenChange={setShowLeadModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building className="w-5 h-5" />
              {selectedLead?.school_name}
            </DialogTitle>
            <DialogDescription>Complete lead history and details</DialogDescription>
          </DialogHeader>
          
          {selectedLead && (
            <div className="space-y-6">
              {/* Lead Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-500" />
                    <span>{selectedLead.address}, {selectedLead.city}, {selectedLead.state}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-500" />
                    <span>{selectedLead.poc_phone}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-500" />
                    <span>{selectedLead.poc_email || 'N/A'}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <p><strong>POC:</strong> {selectedLead.poc_name} ({selectedLead.poc_designation})</p>
                  <p><strong>School Type:</strong> {selectedLead.school_type}</p>
                  <p><strong>Students:</strong> {selectedLead.num_students || 'N/A'}</p>
                  <p><strong>Expected Value:</strong> ₹{(selectedLead.expected_deal_value || 0).toLocaleString()}</p>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Current Status</p>
                  <Badge className={STATUS_COLORS[selectedLead.status]}>{selectedLead.status}</Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Assigned To</p>
                  <p className="font-medium">{selectedLead.assigned_to_name || 'Unassigned'}</p>
                </div>
                <Button variant="outline" onClick={() => setShowAssignModal(true)}>
                  <UserPlus className="w-4 h-4 mr-2" /> Reassign
                </Button>
              </div>

              {selectedLead.products_interested?.length > 0 && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Products Interested</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedLead.products_interested.map((p, i) => (
                      <Badge key={i} variant="outline">{p}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Activity History */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <History className="w-5 h-5" /> Activity History
                </h3>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {leadHistory.map((activity, idx) => (
                    <div key={idx} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex flex-col items-center">
                        <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                        {idx < leadHistory.length - 1 && <div className="w-0.5 flex-1 bg-gray-300 mt-1"></div>}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-sm">{activity.activity_type.replace('_', ' ').toUpperCase()}</p>
                          <span className="text-xs text-gray-500">
                            {new Date(activity.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
                        <p className="text-xs text-gray-400 mt-1">by {activity.employee_name}</p>
                        {activity.new_status && (
                          <Badge className={`${STATUS_COLORS[activity.new_status]} mt-1`}>
                            {activity.new_status}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Lead</DialogTitle>
            <DialogDescription>Select an employee to assign this lead to</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Lead</Label>
              <p className="font-medium">{selectedLead?.school_name}</p>
            </div>
            <div>
              <Label>Assign To</Label>
              <Select value={assignEmployeeId} onValueChange={setAssignEmployeeId}>
                <SelectTrigger><SelectValue placeholder="Select Employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.name} - {e.position}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleAssignLead} className="w-full bg-orange-600 hover:bg-orange-700" disabled={!assignEmployeeId || isSaving}>
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Assign Lead
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesAdminPortal;
